import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Linking, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { chatsApi } from '@/lib/api/chats';

export default function LocalBriefing({ enabled, contextKey }) {
  const { colors } = useTheme();
  const [width, setWidth] = useState(0);
  const compact = width < 720;
  const [revision, setRevision] = useState(0);
  const key = `${contextKey}:${enabled}`;
  const current = useRef({ key, alive: true }); current.current.key = key;
  const savedSelection = useRef(null);
  useEffect(() => { current.current.alive = true; return () => { current.current.alive = false; }; }, []);
  const [result, setResult] = useState({ key, data: null });
  const [editing, setEditing] = useState(false), [draft, setDraft] = useState(''), [saving, setSaving] = useState(false), [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => {
    let alive = true;
    setResult(previous => previous.key === key ? previous : { key, data: null });
    setEditing(false); setError(''); setSaving(false); setRefreshing(enabled);
    if (enabled) chatsApi.fetchLocalBriefing().then(data => {
      if (!alive) return;
      if (data.error) throw new Error(data.message || 'Could not refresh the local briefing.');
      const saved = savedSelection.current;
      if (data.available && saved?.key === key && (data.location !== saved.location || data.locationSource !== saved.locationSource)) {
        throw new Error('Your location was saved, but the briefing has not refreshed yet. Please retry.');
      }
      setResult({ key, data: data.available ? data : null });
      savedSelection.current = null;
    }).catch(err => { if (alive) setError(err.message || 'Could not refresh the local briefing.'); })
      .finally(() => { if (alive) setRefreshing(false); });
    return () => { alive = false; };
  }, [enabled, key, revision]);
  const data = result.key === key ? result.data : null;
  if (!enabled || !data) return null;
  const save = async location => {
    const activeKey = key;
    setSaving(true); setError('');
    try {
      const response = await chatsApi.setLocalBriefingLocation(location);
      if (response.error || !response.ok) throw new Error(response.message || 'Could not update this location.');
      if (current.current.alive && current.current.key === activeKey) {
        // Support a previous server's {ok:true} acknowledgement during rollout.
        const selection = { location: response.location || location.trim() || data.memoryLocation,
          locationSource: response.locationSource || (location.trim() ? 'custom' : 'memory'),
          memoryLocation: response.memoryLocation === undefined ? data.memoryLocation : response.memoryLocation };
        savedSelection.current = { key, ...selection };
        // Keep the section mounted, without showing the previous city's data.
        setResult({ key, data: { ...data, ...selection, weather: null, news: [] } });
        setRevision(value => value + 1);
      }
    } catch (err) { if (current.current.alive && current.current.key === activeKey) setError(err.message || 'Could not update this location.'); }
    finally { if (current.current.alive && current.current.key === activeKey) setSaving(false); }
  };
  const button = (label, action) => <Pressable disabled={saving} accessibilityRole="button" accessibilityLabel={label} onPress={action} style={styles.button}><Text size="xs" color="secondary">{label}</Text></Pressable>;
  const border = { borderColor: colors.border_color_secondary };
  return <View testID="local-briefing" onLayout={event => setWidth(event.nativeEvent.layout.width)} style={[styles.root, border]}>
    <View style={[styles.header, compact && styles.headerCompact]}>
      <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
        <Text weight="medium">{data.location}</Text>
        <Text size="xs" color="secondary">{data.locationSource === 'memory' ? `From memory: ${data.memoryLocation}` : `Set for this section${data.memoryLocation ? ` · Memory: ${data.memoryLocation}` : ''}`} · Free local briefing</Text>
      </View>
      {button('Change location', () => { setDraft(data.location); setEditing(!editing); })}
    </View>
    {editing && <View style={[styles.edit, { backgroundColor: colors.solid_color_secondary }]}>
      <Text size="s">City and region</Text>
      <TextInput accessibilityLabel="City and region" value={draft} onChangeText={setDraft} maxLength={120} editable={!saving}
        placeholder="City, state or region, country" placeholderTextColor={colors.text_color_secondary}
        style={[styles.input, border, { color: colors.text_color_primary }]} />
      <Text size="xs" color="secondary">Changes only this section. Your saved memory stays unchanged.</Text>
      <View style={styles.row}>{button('Save location', () => save(draft))}{data.locationSource === 'custom' && button('Use memory location', () => save(''))}{button('Cancel', () => setEditing(false))}{saving && <ActivityIndicator color={colors.text_color_secondary} />}</View>
      {!!error && <Text size="s" color="secondary" accessibilityRole="alert">{error}</Text>}
    </View>}
    {refreshing ? <View style={styles.row}><ActivityIndicator color={colors.text_color_secondary} /><Text size="s" color="secondary">Updating local briefing…</Text></View> : <View style={[styles.content, compact && styles.contentCompact]}>
      <View style={styles.weather}>
        {data.weather ? <Pressable accessibilityRole="link" accessibilityLabel="Weather source" onPress={() => Linking.openURL(data.weather.url).catch(() => setError('Could not open the weather source.'))} style={styles.temperature}>
          <Icon name="sun" size={21} color={colors.text_color_secondary} />
          <Text weight="medium" style={{ fontSize: 23 }}>{Math.round(data.weather.temperature)}°{data.weather.unit}</Text>
          <Text size="s" color="secondary">{data.weather.condition}</Text>
        </Pressable> : <Text size="s" color="secondary">Weather is unavailable right now.</Text>}
      </View>
      <View style={[styles.news, compact && styles.newsCompact, compact && border]}>
        {data.news.length ? <>
          {data.news.map(article => <Pressable key={article.url} accessibilityRole="link" accessibilityLabel={article.title}
            onPress={() => Linking.openURL(article.url).catch(() => setError('Could not open this story.'))} style={styles.article}>
            <Text size="s" style={{ lineHeight: 20 }}>{article.title} ↗</Text>
            <Text size="xs" color="secondary">{article.source} · {new Date(article.publishedAt).toLocaleDateString()}</Text>
          </Pressable>)}

        </> : <Text size="s" color="secondary">No recent local headlines are available.</Text>}
      </View>
    </View>}
    {!!error && !editing && <View style={styles.row}><Text size="s" color="secondary" accessibilityRole="alert">{error}</Text>{button('Retry local briefing', () => setRevision(value => value + 1))}</View>}
  </View>;
}
const styles = StyleSheet.create({
  root: { borderWidth: 1, borderRadius: 14, paddingHorizontal: 16, paddingVertical: 16, gap: 12, marginBottom: 16 },
  header: { flexDirection: 'row', gap: 8, alignItems: 'center' },
  headerCompact: { alignItems: 'flex-start' },
  contentCompact: { flexDirection: 'column', gap: 14 },
  newsCompact: { width: '100%', flex: undefined, minWidth: 0, borderTopWidth: StyleSheet.hairlineWidth, paddingTop: 16, gap: 18 },
  content: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' },
  weather: { flexShrink: 1, paddingVertical: 4 },
  temperature: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 9 },
  news: { flex: 1, minWidth: 220, gap: 16 }, article: { gap: 5 },
  button: { paddingHorizontal: 4, minHeight: 28, justifyContent: 'center' },
  edit: { padding: 16, borderRadius: 10, gap: 12 }, input: { padding: 12, borderWidth: 1, borderRadius: 8, fontSize: 16 }, row: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
});
